import React from "react";
import Button from "../../ui/Button";
import { Card, CardBody, CardHeader } from "../../ui/Card";
import Input from "../../ui/Input";
import { createJob, listJobs } from "../../api/jobs";
import {
  createMitigationAssessment,
  deleteMitigationPhotos,
  deleteMitigationSketches,
  downloadMitigationExportPdf,
  downloadMitigationExportXlsx,
  getMitigationAssessment,
  listMitigationPhotos,
  runMitigationAssessment,
  setMitigationAffectedRooms,
  uploadMitigationPhotos,
  uploadMitigationSketches,
} from "../../api/mitigation";

const LOSS_CAUSE_OPTIONS = ["water", "fire", "smoke"];
const WATER_CATEGORY_OPTIONS = ["cat1", "cat2", "cat3", "unknown"];
const WATER_CLASS_OPTIONS = ["class1", "class2", "class3", "class4", "unknown"];

const DEFAULT_ASSUMPTIONS = {
  default_ceiling_height_ft: 8,
  dehumidifier_type: "LGR",
  monitoring_frequency: "daily",
  industry_profile: "industry_typical_v1",
  unknown_input_policy: "widen_ranges",
  assumption_notes: "",
};

function humanizeTaskId(taskId) {
  const raw = String(taskId || "").trim().toLowerCase();
  if (!raw) return "Mitigation Task";
  const TASK_LABELS = {
    "mitigation.equipment_setup.air_movers": "Air Mover Setup and Operation",
    "mitigation.equipment_setup.dehumidifiers": "Dehumidifier Setup and Operation",
    "mitigation.monitoring.daily": "Daily Monitoring and Moisture Checks",
    "mitigation.containment.setup": "Containment Setup",
    "mitigation.treatment.antimicrobial_application": "Antimicrobial Application",
    "mitigation.air_filtration.hepa_air_scrubber": "HEPA Air Scrubber",
    "mitigation.smoke.assessment_documentation_per_room": "Smoke Damage Assessment and Documentation",
    "mitigation.smoke.hepa_vacuum_and_surface_cleaning": "HEPA Vacuum and Surface Cleaning",
    "mitigation.smoke.odor_control_deodorization": "Odor Control and Deodorization",
    "mitigation.fire.soot_cleanup_detail": "Detailed Soot Cleanup",
    "mitigation.facilitation.content_manipulation": "Content Manipulation",
    "mitigation.facilitation.detach_reset.toilet": "Detach and Reset Toilet",
    "mitigation.facilitation.detach_reset.vanity": "Detach and Reset Vanity",
    "mitigation.facilitation.detach_reset.baseboards": "Detach and Reset Baseboards",
  };
  if (TASK_LABELS[raw]) return TASK_LABELS[raw];
  return raw
    .replace(/^mitigation\./, "")
    .replace(/[._]/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function includesRoom(rooms, target) {
  const t = String(target || "").trim().toLowerCase();
  return Array.isArray(rooms) && rooms.some((r) => String(r || "").trim().toLowerCase() === t);
}

function isLikelyUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value || "").trim());
}

function normalizeRoomKey(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function findBestRoomArea(room, roomAreaMeta) {
  const key = normalizeRoomKey(room);
  if (!key) return null;
  if (roomAreaMeta[key]) return roomAreaMeta[key];
  const keys = Object.keys(roomAreaMeta || {});
  for (const candidate of keys) {
    if (!candidate) continue;
    if (candidate.includes(key) || key.includes(candidate)) {
      return roomAreaMeta[candidate];
    }
  }
  const roomTokens = new Set(key.split(" ").filter(Boolean));
  let best = null;
  let bestScore = 0;
  for (const candidate of keys) {
    const cTokens = new Set(String(candidate).split(" ").filter(Boolean));
    let overlap = 0;
    roomTokens.forEach((t) => {
      if (cTokens.has(t)) overlap += 1;
    });
    if (overlap > bestScore) {
      bestScore = overlap;
      best = roomAreaMeta[candidate];
    }
  }
  return bestScore >= 1 ? best : null;
}

function fmtNum(value, unit = "") {
  const n = Number(value);
  if (!Number.isFinite(n)) return "n/a";
  const u = String(unit || "").trim().toUpperCase();
  if (u === "SF" || u === "SQFT" || u === "FT2") return String(Math.round(n));
  return n % 1 === 0 ? String(n) : n.toFixed(1);
}

function formatQuantityBasis(quantityBasis) {
  const qb = quantityBasis && typeof quantityBasis === "object" ? quantityBasis : {};
  const q = qb.quantity_range && typeof qb.quantity_range === "object" ? qb.quantity_range : {};
  const min = Number(q.min);
  const max = Number(q.max);
  const unit = String(qb.unit || "").trim() || "EA";
  if (Number.isFinite(min) && Number.isFinite(max)) {
    if (min === max) return `${fmtNum(min, unit)} ${unit}`;
    return `${fmtNum(min, unit)} - ${fmtNum(max, unit)} ${unit}`;
  }
  return `See rationale (${unit})`;
}

function formatMappings(mapping) {
  const m = mapping && typeof mapping === "object" ? mapping : {};
  const x = String(m.xactimate_code || "").trim() || "Pending";
  const s = String(m.symbility_code || "").trim() || "Pending";
  return { xactimate: x, symbility: s };
}

export default function MitigationStation() {
  const [jobs, setJobs] = React.useState([]);
  const [loadingJobs, setLoadingJobs] = React.useState(true);
  const [jobPickerId, setJobPickerId] = React.useState("");
  const [manualJobId, setManualJobId] = React.useState("");

  const [form, setForm] = React.useState({
    loss_causes: ["water"],
    water_category: "unknown",
    water_class: "unknown",
    affected_area: "100",
    affected_area_unit: "SF",
    ceiling_height_ft: "8",
    containment_used: false,
    notes: "",
  });
  const [assumptions, setAssumptions] = React.useState({ ...DEFAULT_ASSUMPTIONS });

  const [error, setError] = React.useState("");
  const [busyCreate, setBusyCreate] = React.useState(false);
  const [busyRun, setBusyRun] = React.useState(false);
  const [busyPhotoUpload, setBusyPhotoUpload] = React.useState(false);
  const [busySketchUpload, setBusySketchUpload] = React.useState(false);
  const [busyPhotoDelete, setBusyPhotoDelete] = React.useState(false);
  const [busySketchDelete, setBusySketchDelete] = React.useState(false);
  const [busyRooms, setBusyRooms] = React.useState(false);
  const [busyExportPdf, setBusyExportPdf] = React.useState(false);
  const [busyExportXlsx, setBusyExportXlsx] = React.useState(false);
  const [copiedCodeKey, setCopiedCodeKey] = React.useState("");
  const [assessmentId, setAssessmentId] = React.useState("");
  const [resultPayload, setResultPayload] = React.useState(null);
  const [affectedRooms, setAffectedRooms] = React.useState([]);
  const [photos, setPhotos] = React.useState([]);
  const [photoFiles, setPhotoFiles] = React.useState([]);
  const [sketchFiles, setSketchFiles] = React.useState([]);
  const [selectedPhotoIds, setSelectedPhotoIds] = React.useState([]);
  const [selectedSketchIds, setSelectedSketchIds] = React.useState([]);

  const manualInput = String(manualJobId || "").trim();
  const selectedJobId = isLikelyUuid(manualInput) ? manualInput : String(jobPickerId || "").trim();
  const selectedLossCauses = Array.isArray(form.loss_causes) ? form.loss_causes : [];
  const isWaterLoss = selectedLossCauses.includes("water");
  const isSmokeLoss = selectedLossCauses.includes("smoke");
  const activeJobId = selectedJobId || String(resultPayload?.assessment?.job_id || "").trim();

  const hydrateAssessment = React.useCallback((payload) => {
    setResultPayload(payload || null);
    const rooms = payload?.assessment?.affected_rooms;
    setAffectedRooms(Array.isArray(rooms) ? rooms : []);
    const sketches = Array.isArray(payload?.assessment?.sketches) ? payload.assessment.sketches : [];
    const allowed = new Set(sketches.map((s) => String(s?.id || "").trim()).filter(Boolean));
    setSelectedSketchIds((prev) => prev.filter((id) => allowed.has(String(id || "").trim())));
  }, []);

  const refreshJobs = React.useCallback(async () => {
    setLoadingJobs(true);
    try {
      const rows = await listJobs({ limit: 300, offset: 0 });
      setJobs(Array.isArray(rows) ? rows : []);
      if (!jobPickerId && Array.isArray(rows) && rows.length > 0) {
        setJobPickerId(String(rows[0].id || ""));
      }
    } catch (_e) {
      setJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  }, [jobPickerId]);

  const refreshPhotos = React.useCallback(async () => {
    if (!activeJobId) {
      setPhotos([]);
      return;
    }
    try {
      const res = await listMitigationPhotos(activeJobId);
      const rows = Array.isArray(res?.photos) ? res.photos : [];
      setPhotos(rows);
      const allowed = new Set(rows.map((p) => String(p?.id || "").trim()).filter(Boolean));
      setSelectedPhotoIds((prev) => prev.filter((id) => allowed.has(String(id || "").trim())));
    } catch (e) {
      setPhotos([]);
      setSelectedPhotoIds([]);
      setError(e?.message || "Failed to refresh photos for this job.");
    }
  }, [activeJobId]);

  React.useEffect(() => {
    refreshJobs();
  }, [refreshJobs]);

  React.useEffect(() => {
    refreshPhotos();
  }, [refreshPhotos]);

  const onCreateAssessment = async () => {
    setError("");
    setBusyCreate(true);
    setResultPayload(null);
    try {
      let effectiveJobId = selectedJobId;
      if (!effectiveJobId) {
        if (!manualInput) {
          throw new Error("Select a job or enter a job name/id.");
        }
        const byName = (jobs || []).find((j) => String(j?.name || "").trim().toLowerCase() === manualInput.toLowerCase());
        if (byName?.id) {
          effectiveJobId = String(byName.id);
        } else {
          const createdJob = await createJob(manualInput);
          effectiveJobId = String(createdJob?.id || "").trim();
          if (!effectiveJobId) {
            throw new Error("Failed to create job from the manual name.");
          }
          setJobPickerId(effectiveJobId);
          setManualJobId("");
          await refreshJobs();
        }
      }
      const payload = {
        job_id: effectiveJobId,
        loss_causes: selectedLossCauses,
        loss_cause: selectedLossCauses[0] || "other",
        water_category: isWaterLoss ? form.water_category : "unknown",
        water_class: isWaterLoss ? form.water_class : "unknown",
        affected_area: Number(form.affected_area),
        affected_area_unit: String(form.affected_area_unit || "SF").trim() || "SF",
        ceiling_height_ft: Number(form.ceiling_height_ft),
        containment_used: Boolean(form.containment_used),
        notes: String(form.notes || "").trim() || null,
        assumptions: {
          ...assumptions,
          default_ceiling_height_ft: Number(assumptions.default_ceiling_height_ft || 8),
        },
        affected_rooms: isSmokeLoss ? affectedRooms : [],
      };
      const created = await createMitigationAssessment(payload);
      const id = String(created?.id || "").trim();
      if (!id) {
        throw new Error("Backend returned no assessment id.");
      }
      setAssessmentId(id);
      const loaded = await getMitigationAssessment(id);
      hydrateAssessment(loaded);
    } catch (e) {
      setError(e?.message || "Failed to create assessment");
    } finally {
      setBusyCreate(false);
    }
  };

  const onUploadPhotos = async () => {
    setError("");
    setBusyPhotoUpload(true);
    try {
      if (!activeJobId) {
        throw new Error("Select a job first.");
      }
      if (!photoFiles.length) {
        throw new Error("Choose one or more photos first.");
      }
      await uploadMitigationPhotos(activeJobId, photoFiles);
      setPhotoFiles([]);
      await refreshPhotos();
    } catch (e) {
      setError(e?.message || "Failed to upload photos");
    } finally {
      setBusyPhotoUpload(false);
    }
  };

  const onUploadSketch = async () => {
    setError("");
    setBusySketchUpload(true);
    try {
      if (!assessmentId) {
        throw new Error("Create the mitigation assessment before uploading sketch.");
      }
      if (!sketchFiles.length) {
        throw new Error("Choose one or more sketch files first.");
      }
      await uploadMitigationSketches(assessmentId, sketchFiles);
      const loaded = await getMitigationAssessment(assessmentId);
      hydrateAssessment(loaded);
      setSketchFiles([]);
    } catch (e) {
      setError(e?.message || "Failed to upload sketch");
    } finally {
      setBusySketchUpload(false);
    }
  };

  const onRunAssessment = async () => {
    setError("");
    setBusyRun(true);
    try {
      if (!assessmentId) {
        throw new Error("Create an assessment first.");
      }
      await runMitigationAssessment(assessmentId);
      const loaded = await getMitigationAssessment(assessmentId);
      hydrateAssessment(loaded);
    } catch (e) {
      setError(e?.message || "Failed to run mitigation assessment");
    } finally {
      setBusyRun(false);
    }
  };

  const onToggleAffectedRoom = async (room) => {
    if (!assessmentId || !isSmokeLoss) return;
    const key = String(room || "").trim().toLowerCase();
    const next = affectedRooms.some((r) => String(r || "").trim().toLowerCase() === key)
      ? affectedRooms.filter((r) => String(r || "").trim().toLowerCase() !== key)
      : [...affectedRooms, room];
    setBusyRooms(true);
    setError("");
    try {
      await setMitigationAffectedRooms(assessmentId, next);
      setAffectedRooms(next);
    } catch (e) {
      setError(e?.message || "Failed to update affected rooms");
    } finally {
      setBusyRooms(false);
    }
  };

  const onSetAllAffectedRooms = async (allSelected) => {
    if (!assessmentId || !isSmokeLoss) return;
    const targetRooms = allSelected ? [...sketchRooms] : [];
    setBusyRooms(true);
    setError("");
    try {
      await setMitigationAffectedRooms(assessmentId, targetRooms);
      setAffectedRooms(targetRooms);
    } catch (e) {
      setError(e?.message || "Failed to update affected rooms");
    } finally {
      setBusyRooms(false);
    }
  };

  const onTogglePhotoSelection = (photoId, checked) => {
    const id = String(photoId || "").trim();
    if (!id) return;
    setSelectedPhotoIds((prev) => {
      const set = new Set(prev.map((x) => String(x || "").trim()).filter(Boolean));
      if (checked) set.add(id);
      else set.delete(id);
      return Array.from(set);
    });
  };

  const onToggleSketchSelection = (sketchId, checked) => {
    const id = String(sketchId || "").trim();
    if (!id) return;
    setSelectedSketchIds((prev) => {
      const set = new Set(prev.map((x) => String(x || "").trim()).filter(Boolean));
      if (checked) set.add(id);
      else set.delete(id);
      return Array.from(set);
    });
  };

  const onDeleteSelectedPhotos = async () => {
    setError("");
    setBusyPhotoDelete(true);
    try {
      if (!activeJobId) throw new Error("Select a job first.");
      if (!selectedPhotoIds.length) throw new Error("Select one or more photos to delete.");
      await deleteMitigationPhotos(activeJobId, selectedPhotoIds);
      setSelectedPhotoIds([]);
      await refreshPhotos();
    } catch (e) {
      setError(e?.message || "Failed to delete photos");
    } finally {
      setBusyPhotoDelete(false);
    }
  };

  const onDeleteSelectedSketches = async () => {
    setError("");
    setBusySketchDelete(true);
    try {
      if (!assessmentId) throw new Error("Create/select an assessment first.");
      if (!selectedSketchIds.length) throw new Error("Select one or more sketches to delete.");
      await deleteMitigationSketches(assessmentId, selectedSketchIds);
      setSelectedSketchIds([]);
      const loaded = await getMitigationAssessment(assessmentId);
      hydrateAssessment(loaded);
    } catch (e) {
      setError(e?.message || "Failed to delete sketches");
    } finally {
      setBusySketchDelete(false);
    }
  };
  const onCopyCode = React.useCallback(async (code, key) => {
    const text = String(code || "").trim();
    if (!text || text.toLowerCase() === "pending") return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCodeKey(String(key || ""));
      setTimeout(() => setCopiedCodeKey(""), 1500);
    } catch (_e) {
      // no-op
    }
  }, []);
  const onExportPdf = async () => {
    setError("");
    setBusyExportPdf(true);
    try {
      if (!assessmentId) throw new Error("Create/run an assessment first.");
      await downloadMitigationExportPdf(assessmentId);
    } catch (e) {
      setError(e?.message || "Failed to export PDF");
    } finally {
      setBusyExportPdf(false);
    }
  };
  const onExportXlsx = async () => {
    setError("");
    setBusyExportXlsx(true);
    try {
      if (!assessmentId) throw new Error("Create/run an assessment first.");
      await downloadMitigationExportXlsx(assessmentId);
    } catch (e) {
      setError(e?.message || "Failed to export Excel");
    } finally {
      setBusyExportXlsx(false);
    }
  };


  const outputs = resultPayload?.result?.outputs || null;
  const equipment = outputs?.equipment_recommendations || null;
  const lineItems = outputs?.suggested_mitigation_line_items || null;
  const narrative = outputs?.narrative || null;
  const inputsUsed = equipment?.rationale?.inputs_snapshot || {};
  const resultLossCauses = Array.isArray(inputsUsed?.loss_causes) && inputsUsed.loss_causes.length > 0
    ? inputsUsed.loss_causes.map((x) => String(x || "").toLowerCase())
    : selectedLossCauses.map((x) => String(x || "").toLowerCase());
  const isSmokeResult = resultLossCauses.includes("smoke");
  const isWaterResult = resultLossCauses.includes("water");
  const areaBasis = String(inputsUsed?.area_basis || "user_reported_affected_area");
  const usedArea = inputsUsed?.affected_area;
  const reportedArea = inputsUsed?.reported_affected_area;
  const sketchArea = inputsUsed?.sketch_measured_area_sf;
  const sketchMeta = resultPayload?.assessment?.sketch || {};
  const sketches = Array.isArray(resultPayload?.assessment?.sketches) ? resultPayload.assessment.sketches : [];
  const sketchRooms = Array.from(
    new Set(
      [
        ...sketches.flatMap((sk) =>
          Array.isArray(sk?.analysis?.rooms_detected) ? sk.analysis.rooms_detected.map((x) => String(x || "").trim()) : []
        ),
        ...(Array.isArray(sketchMeta?.analysis?.rooms_detected) ? sketchMeta.analysis.rooms_detected.map((x) => String(x || "").trim()) : []),
      ].filter(Boolean)
    )
  );
  const roomsForResults = Array.from(
    new Set(
      [
        ...(Array.isArray(inputsUsed?.affected_rooms) ? inputsUsed.affected_rooms : []),
        ...sketchRooms,
      ]
      .map((x) => String(x || "").trim())
      .filter(Boolean)
    )
  );
  const roomAreaMeta = React.useMemo(() => {
    const map = {};
    const addRoom = (roomName, areaSf, source) => {
      const key = normalizeRoomKey(roomName);
      if (!key || !Number.isFinite(Number(areaSf)) || Number(areaSf) <= 0) return;
      const area = Number(areaSf);
      const existing = map[key];
      if (!existing || area > existing.area_sf) {
        map[key] = { area_sf: area, source };
      }
    };
    sketches.forEach((sk) => {
      const roomAreas = sk?.analysis?.vision_area_estimate?.room_areas;
      if (Array.isArray(roomAreas)) {
        roomAreas.forEach((row) => addRoom(row?.room, row?.area_sf, "sketch"));
      }
    });
    return map;
  }, [sketches]);
  const hasLineItems = Array.isArray(lineItems?.items) && lineItems.items.length > 0;
  const showRoomOrganizedItems = hasLineItems && roomsForResults.length > 0;
  const showFlatItems = hasLineItems && roomsForResults.length < 1;

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <Card>
        <CardHeader>
          <h1 className="text-2xl font-semibold text-slate-900">Mitigation Station</h1>
          <p className="mt-1 text-sm text-slate-500">
            Deterministic mitigation recommendations with explainable rationale for contractors and adjusters.
          </p>
        </CardHeader>
        <CardBody className="space-y-4">
          {error ? <p className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-sm text-rose-700">{error}</p> : null}

          <div className="rounded-xl border border-slate-200 p-4">
            <h2 className="text-lg font-semibold text-slate-900">Create Mitigation Assessment</h2>
            <p className="mt-1 text-sm text-slate-500">Capture minimum defensible inputs before running deterministic engines.</p>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="text-sm text-slate-700">
                Job selection
                <select
                  className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800"
                  disabled={loadingJobs}
                  value={jobPickerId}
                  onChange={(e) => setJobPickerId(e.target.value)}
                >
                  <option value="">{loadingJobs ? "Loading jobs..." : "Select job id"}</option>
                  {jobs.map((job) => (
                    <option key={String(job?.id || "")} value={String(job?.id || "")}>
                      {String(job?.name || "(untitled)")} ({String(job?.id || "")})
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm text-slate-700">
                Or enter job id manually
                <Input
                  className="mt-1"
                  value={manualJobId}
                  onChange={(e) => setManualJobId(e.target.value)}
                  placeholder="job-id"
                />
              </label>
            </div>

            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <div className="sm:col-span-2 lg:col-span-3">
                <p className="text-sm text-slate-700">Loss type (select one or more)</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {LOSS_CAUSE_OPTIONS.map((opt) => {
                    const active = selectedLossCauses.includes(opt);
                    return (
                      <Button
                        key={opt}
                        size="sm"
                        variant={active ? "primary" : "secondary"}
                        onClick={() => {
                          setForm((prev) => {
                            const current = Array.isArray(prev.loss_causes) ? prev.loss_causes : [];
                            const next = current.includes(opt)
                              ? current.filter((x) => x !== opt)
                              : [...current, opt];
                            return { ...prev, loss_causes: next.length > 0 ? next : [opt] };
                          });
                        }}
                      >
                        {opt[0].toUpperCase() + opt.slice(1)}
                      </Button>
                    );
                  })}
                </div>
              </div>
              {isWaterLoss ? (
                <>
                  <label className="text-sm text-slate-700">
                    Water category
                    <select
                      className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800"
                      value={form.water_category}
                      onChange={(e) => setForm((prev) => ({ ...prev, water_category: e.target.value }))}
                    >
                      {WATER_CATEGORY_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </label>
                  <label className="text-sm text-slate-700">
                    Water class
                    <select
                      className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800"
                      value={form.water_class}
                      onChange={(e) => setForm((prev) => ({ ...prev, water_class: e.target.value }))}
                    >
                      {WATER_CLASS_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </label>
                </>
              ) : null}
              <label className="text-sm text-slate-700">
                Affected area
                <Input
                  className="mt-1"
                  type="number"
                  min="1"
                  step="0.01"
                  value={form.affected_area}
                  onChange={(e) => setForm((prev) => ({ ...prev, affected_area: e.target.value }))}
                />
              </label>
              <label className="text-sm text-slate-700">
                Unit
                <Input
                  className="mt-1"
                  value={form.affected_area_unit}
                  onChange={(e) => setForm((prev) => ({ ...prev, affected_area_unit: e.target.value }))}
                />
              </label>
              <label className="text-sm text-slate-700">
                Ceiling height (ft)
                <Input
                  className="mt-1"
                  type="number"
                  min="1"
                  step="0.1"
                  value={form.ceiling_height_ft}
                  onChange={(e) => setForm((prev) => ({ ...prev, ceiling_height_ft: e.target.value }))}
                />
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 sm:col-span-2 lg:col-span-3">
                <input
                  type="checkbox"
                  checked={Boolean(form.containment_used)}
                  onChange={(e) => setForm((prev) => ({ ...prev, containment_used: e.target.checked }))}
                />
                Containment used
              </label>
            </div>

            {isSmokeLoss ? (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm font-medium text-amber-900">Smoke Loss Rooms (from sketch)</p>
                <p className="mt-1 text-xs text-amber-800">
                  For smoke losses, all rooms detected in sketch are treated as affected by default. Uncheck any room to remove it.
                </p>
                {sketchRooms.length > 0 ? (
                  <div className="mt-2 space-y-2">
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="secondary" disabled={busyRooms || !assessmentId} onClick={() => onSetAllAffectedRooms(true)}>
                        Select All Rooms
                      </Button>
                      <Button size="sm" variant="ghost" disabled={busyRooms || !assessmentId} onClick={() => onSetAllAffectedRooms(false)}>
                        Deselect All Rooms
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {sketchRooms.map((room) => {
                        const checked = affectedRooms.some((r) => String(r).toLowerCase() === String(room).toLowerCase());
                        return (
                          <label key={room} className="inline-flex items-center gap-2 text-sm text-amber-900">
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={busyRooms || !assessmentId}
                              onChange={() => onToggleAffectedRoom(room)}
                            />
                            {room}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-amber-800">Upload a sketch to auto-detect rooms.</p>
                )}
              </div>
            ) : null}

            <label className="mt-3 block text-sm text-slate-700">
              Notes
              <textarea
                className="mt-1 min-h-[80px] w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
                value={form.notes}
                onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
              />
            </label>


            <details className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <summary className="cursor-pointer text-sm font-medium text-slate-800">Assumptions</summary>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="text-sm text-slate-700">
                  Default ceiling height (ft)
                  <Input
                    className="mt-1"
                    type="number"
                    min="1"
                    step="0.1"
                    value={String(assumptions.default_ceiling_height_ft ?? 8)}
                    onChange={(e) => setAssumptions((prev) => ({ ...prev, default_ceiling_height_ft: e.target.value }))}
                  />
                </label>
                <label className="text-sm text-slate-700">
                  Dehumidifier type default
                  <Input
                    className="mt-1"
                    value={String(assumptions.dehumidifier_type ?? "LGR")}
                    onChange={(e) => setAssumptions((prev) => ({ ...prev, dehumidifier_type: e.target.value }))}
                  />
                </label>
                <label className="text-sm text-slate-700">
                  Monitoring frequency
                  <Input
                    className="mt-1"
                    value={String(assumptions.monitoring_frequency ?? "daily")}
                    onChange={(e) => setAssumptions((prev) => ({ ...prev, monitoring_frequency: e.target.value }))}
                  />
                </label>
                <label className="text-sm text-slate-700">
                  Unknown input policy
                  <Input
                    className="mt-1"
                    value={String(assumptions.unknown_input_policy ?? "widen_ranges")}
                    onChange={(e) => setAssumptions((prev) => ({ ...prev, unknown_input_policy: e.target.value }))}
                  />
                </label>
                <label className="text-sm text-slate-700 sm:col-span-2">
                  Assumption notes
                  <textarea
                    className="mt-1 min-h-[70px] w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
                    value={String(assumptions.assumption_notes ?? "")}
                    onChange={(e) => setAssumptions((prev) => ({ ...prev, assumption_notes: e.target.value }))}
                  />
                </label>
              </div>
            </details>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button onClick={onCreateAssessment} disabled={busyCreate}>
                {busyCreate ? "Creating..." : "Create Assessment"}
              </Button>
              <Button variant="secondary" onClick={onRunAssessment} disabled={busyRun || !assessmentId}>
                {busyRun ? "Running..." : "Run Assessment"}
              </Button>
              <Button variant="ghost" onClick={refreshJobs} disabled={loadingJobs}>Refresh Jobs</Button>
            </div>
            {assessmentId ? (
              <p className="mt-2 text-xs font-mono text-slate-600">Assessment ID: {assessmentId}</p>
            ) : null}
          </div>

          <div className="rounded-xl border border-slate-200 p-4">
            <h2 className="text-lg font-semibold text-slate-900">Evidence Inputs (Optional)</h2>
            <p className="mt-1 text-sm text-slate-500">
              Upload photos and sketch files to strengthen evidence tags used by deterministic rules.
              Mitigation photo uploads count toward the Free plan 50-photo upload limit.
            </p>
            <div className="mt-3 grid gap-4 lg:grid-cols-2">
              <div className="rounded-lg border border-slate-200 p-3">
                <p className="text-sm font-medium text-slate-800">Photos (job-level)</p>
                <input
                  className="mt-2 block w-full text-sm"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => setPhotoFiles(Array.from(e.target.files || []))}
                />
                <div className="mt-2 flex items-center gap-2">
                  <Button size="sm" onClick={onUploadPhotos} disabled={busyPhotoUpload || !activeJobId}>
                    {busyPhotoUpload ? "Uploading..." : "Upload Photos"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={refreshPhotos} disabled={!activeJobId}>Refresh</Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setSelectedPhotoIds(photos.map((p) => String(p?.id || "").trim()).filter(Boolean))}
                    disabled={!photos.length}
                  >
                    Select All
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelectedPhotoIds([])} disabled={!selectedPhotoIds.length}>
                    Clear
                  </Button>
                  <Button size="sm" variant="ghost" onClick={onDeleteSelectedPhotos} disabled={!selectedPhotoIds.length || busyPhotoDelete}>
                    {busyPhotoDelete ? "Removing..." : `Remove Selected (${selectedPhotoIds.length})`}
                  </Button>
                </div>
                <p className="mt-2 text-xs text-slate-600">Detected photos for this job: {photos.length}</p>
                {photos.length > 0 ? (
                  <ul className="mt-2 max-h-40 overflow-auto rounded border border-slate-200 p-2 text-xs text-slate-700">
                    {photos.slice(0, 20).map((p) => (
                      <li key={p.id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedPhotoIds.includes(String(p.id || ""))}
                          onChange={(e) => onTogglePhotoSelection(p.id, e.target.checked)}
                        />
                        <span>{p.filename || p.id} | {p.room || "room?"} | {p.damage_type || "damage?"}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
              <div className="rounded-lg border border-slate-200 p-3">
                <p className="text-sm font-medium text-slate-800">Sketches/Documents (job-level persistence)</p>
                <input
                  className="mt-2 block w-full text-sm"
                  type="file"
                  accept=".pdf,image/*"
                  multiple
                  onChange={(e) => setSketchFiles(Array.from(e.target.files || []))}
                />
                <div className="mt-2 flex items-center gap-2">
                  <Button size="sm" onClick={onUploadSketch} disabled={busySketchUpload || !assessmentId}>
                    {busySketchUpload ? "Uploading..." : "Upload Sketches"}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      setSelectedSketchIds(
                        sketches.map((s) => String(s?.id || "").trim()).filter(Boolean)
                      )
                    }
                    disabled={!sketches.length}
                  >
                    Select All
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelectedSketchIds([])} disabled={!selectedSketchIds.length}>
                    Clear
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={onDeleteSelectedSketches}
                    disabled={!selectedSketchIds.length || busySketchDelete || !assessmentId}
                  >
                    {busySketchDelete ? "Removing..." : `Remove Selected (${selectedSketchIds.length})`}
                  </Button>
                </div>
                {sketches.length > 0 ? (
                  <div className="mt-2 rounded border border-slate-200 bg-slate-50 p-2 text-xs text-slate-700">
                    <p>Uploaded sketches: {sketches.length}</p>
                    <ul className="mt-1 max-h-32 overflow-auto space-y-1">
                      {sketches.map((sk, idx) => (
                        <li key={String(sk.id || `${sk.filename || "sketch"}-${idx}`)} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedSketchIds.includes(String(sk?.id || ""))}
                            onChange={(e) => onToggleSketchSelection(sk?.id, e.target.checked)}
                          />
                          <span>
                            {sk.filename} | {sk.uploaded_at || "n/a"} | tags:{" "}
                            {Array.isArray(sk?.analysis?.tags) ? (sk.analysis.tags.join(", ") || "none") : "none"}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : sketchMeta?.filename ? (
                  <div className="mt-2 rounded border border-slate-200 bg-slate-50 p-2 text-xs text-slate-700">
                    <p>Uploaded sketch: {sketchMeta.filename}</p>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {outputs ? (
            <div className="grid gap-4">
              <Card>
                <CardHeader>
                  <h3 className="text-lg font-semibold text-slate-900">Run Summary</h3>
                </CardHeader>
                <CardBody className="text-sm text-slate-800">
                  <div className="mb-2 flex flex-wrap gap-2">
                    <Button size="sm" variant="secondary" onClick={onExportPdf} disabled={busyExportPdf || !resultPayload?.result}>
                      {busyExportPdf ? "Exporting PDF..." : "Export PDF"}
                    </Button>
                    <Button size="sm" variant="secondary" onClick={onExportXlsx} disabled={busyExportXlsx || !resultPayload?.result}>
                      {busyExportXlsx ? "Exporting Excel..." : "Export Excel"}
                    </Button>
                  </div>
                  <p>Area used for calculations: <span className="font-semibold">{usedArea} {inputsUsed?.affected_area_unit || "SF"}</span></p>
                  <p className="mt-1">Area basis: <span className="font-semibold">{areaBasis}</span></p>
                  {reportedArea !== undefined && reportedArea !== null ? (
                    <p className="mt-1">User-reported area: {reportedArea} {inputsUsed?.affected_area_unit || "SF"}</p>
                  ) : null}
                  {sketchArea !== undefined && sketchArea !== null ? (
                    <p className="mt-1">Sketch-measured area: {sketchArea} SF</p>
                  ) : null}
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <h3 className="text-lg font-semibold text-slate-900">Equipment Recommendations</h3>
                </CardHeader>
                <CardBody className="space-y-2 text-sm text-slate-800">
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div className="rounded-lg border border-slate-200 p-2">
                      <p className="text-xs uppercase text-slate-500">Air Movers</p>
                      {isSmokeResult ? (
                        <p className="text-base font-semibold">Not Applicable (Smoke Loss)</p>
                      ) : (
                        <>
                          <p className="text-base font-semibold">{equipment?.recommended_air_movers?.min} - {equipment?.recommended_air_movers?.max}</p>
                          <p className="text-xs text-slate-600">Neutral: {equipment?.recommended_air_movers?.neutral}</p>
                        </>
                      )}
                    </div>
                    <div className="rounded-lg border border-slate-200 p-2">
                      <p className="text-xs uppercase text-slate-500">Dehumidifiers ({equipment?.recommended_dehumidifiers?.type})</p>
                      {isSmokeResult ? (
                        <p className="text-base font-semibold">Not Applicable (Smoke Loss)</p>
                      ) : (
                        <>
                          <p className="text-base font-semibold">{equipment?.recommended_dehumidifiers?.min} - {equipment?.recommended_dehumidifiers?.max}</p>
                          <p className="text-xs text-slate-600">Neutral: {equipment?.recommended_dehumidifiers?.neutral}</p>
                        </>
                      )}
                    </div>
                    {isSmokeResult ? (
                      <>
                        <div className="rounded-lg border border-slate-200 p-2">
                          <p className="text-xs uppercase text-slate-500">HEPA Air Scrubbers</p>
                          <p className="text-base font-semibold">{equipment?.recommended_air_scrubbers?.min} - {equipment?.recommended_air_scrubbers?.max}</p>
                          <p className="text-xs text-slate-600">Neutral: {equipment?.recommended_air_scrubbers?.neutral}</p>
                        </div>
                        <div className="rounded-lg border border-slate-200 p-2">
                          <p className="text-xs uppercase text-slate-500">Odor Control Devices</p>
                          <p className="text-base font-semibold">{equipment?.recommended_odor_control_devices?.min} - {equipment?.recommended_odor_control_devices?.max}</p>
                          <p className="text-xs text-slate-600">Neutral: {equipment?.recommended_odor_control_devices?.neutral}</p>
                        </div>
                      </>
                    ) : null}
                    {isWaterResult ? (
                      <div className="rounded-lg border border-slate-200 p-2">
                        <p className="text-xs uppercase text-slate-500">Drying Days</p>
                        <p className="text-base font-semibold">{equipment?.recommended_days_range?.min} - {equipment?.recommended_days_range?.max}</p>
                      </div>
                    ) : null}
                    <div className="rounded-lg border border-slate-200 p-2">
                      <p className="text-xs uppercase text-slate-500">Monitoring Visits</p>
                      <p className="text-base font-semibold">{equipment?.recommended_monitoring_visits?.min} - {equipment?.recommended_monitoring_visits?.max}</p>
                      <p className="text-xs text-slate-600">Neutral: {equipment?.recommended_monitoring_visits?.neutral}</p>
                    </div>
                  </div>
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <h3 className="text-lg font-semibold text-slate-900">Suggested Mitigation Line Items</h3>
                </CardHeader>
                <CardBody className="space-y-3">
                  {showRoomOrganizedItems ? (
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-slate-800">Organized by Rooms from Sketch</p>
                      {roomsForResults.map((room) => {
                        const roomItems = (lineItems.items || []).filter((item) => {
                          const itemRooms = item?.quantity_basis?.rooms;
                          if (!Array.isArray(itemRooms) || itemRooms.length < 1) return false;
                          return includesRoom(itemRooms, room);
                        });
                        const roomArea = findBestRoomArea(room, roomAreaMeta);
                        return (
                          <div key={room} className="rounded-lg border border-slate-200 p-3">
                            <div className="flex items-center justify-between gap-2">
                              <p className="font-semibold text-slate-900">{room}</p>
                              <p className="text-xs text-slate-600">
                                {roomArea ? `${fmtNum(roomArea.area_sf, "SF")} SF${roomArea.source === "estimated" ? " (estimated)" : ""}` : "SF not available"}
                              </p>
                            </div>
                            {roomItems.length > 0 ? (
                              <ul className="mt-2 space-y-2">
                                {roomItems.map((item, idx) => (
                                  <li key={`${room}-${item?.canonical_task_id || "task"}-${idx}`} className="rounded border border-slate-200 p-2 text-sm">
                                    {(() => {
                                      const mapping = formatMappings(item?.platform_mapping);
                                      return (
                                        <>
                                    <p className="font-medium text-slate-900">{humanizeTaskId(item?.canonical_task_id)}</p>
                                    <p className="text-slate-700">{item?.rationale}</p>
                                    <p className="mt-1 text-xs text-slate-700">Quantity: {formatQuantityBasis(item?.quantity_basis)}</p>
                                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-700">
                                      <span>Xactimate: {mapping.xactimate}</span>
                                      <Button size="sm" variant="ghost" onClick={() => onCopyCode(mapping.xactimate, `${room}-${idx}-x`)}>
                                        {copiedCodeKey === `${room}-${idx}-x` ? "Copied" : "Copy"}
                                      </Button>
                                      <span>|</span>
                                      <span>Symbility: {mapping.symbility}</span>
                                      <Button size="sm" variant="ghost" onClick={() => onCopyCode(mapping.symbility, `${room}-${idx}-s`)}>
                                        {copiedCodeKey === `${room}-${idx}-s` ? "Copied" : "Copy"}
                                      </Button>
                                    </div>
                                        </>
                                      );
                                    })()}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="mt-2 text-xs text-slate-500">No suggestions produced for this room.</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                  {showFlatItems ? (
                    <ul className="space-y-2">
                      {lineItems.items.map((item, idx) => (
                        <li key={`${item?.canonical_task_id || "task"}-${idx}`} className="rounded-lg border border-slate-200 p-3 text-sm">
                          {(() => {
                            const mapping = formatMappings(item?.platform_mapping);
                            return (
                              <>
                          <p className="font-semibold text-slate-900">{humanizeTaskId(item?.canonical_task_id)}</p>
                          <p className="text-slate-700">{item?.rationale}</p>
                          <p className="mt-1 text-xs text-slate-700">Quantity: {formatQuantityBasis(item?.quantity_basis)}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-700">
                            <span>Xactimate: {mapping.xactimate}</span>
                            <Button size="sm" variant="ghost" onClick={() => onCopyCode(mapping.xactimate, `flat-${idx}-x`)}>
                              {copiedCodeKey === `flat-${idx}-x` ? "Copied" : "Copy"}
                            </Button>
                            <span>|</span>
                            <span>Symbility: {mapping.symbility}</span>
                            <Button size="sm" variant="ghost" onClick={() => onCopyCode(mapping.symbility, `flat-${idx}-s`)}>
                              {copiedCodeKey === `flat-${idx}-s` ? "Copied" : "Copy"}
                            </Button>
                          </div>
                              </>
                            );
                          })()}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-500">No line items generated.</p>
                  )}
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <h3 className="text-lg font-semibold text-slate-900">Narrative</h3>
                </CardHeader>
                <CardBody className="space-y-2">
                  <p className="text-sm text-slate-800">{narrative?.paragraph}</p>
                  <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
                    {(narrative?.supporting_facts || []).map((fact, idx) => (
                      <li key={`fact-${idx}`}>{fact}</li>
                    ))}
                  </ul>
                </CardBody>
              </Card>
            </div>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}
